/**
 * Compact current-settings line with Change → ConsentStep compact dialog (non-blocking).
 */
import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isAiCatalogueKey, type AutomationCatalog } from '@/api/automation';
import { useMe } from '@/session/meHooks';
import { ConsentStep } from './ConsentStep';

interface CurrentSettingsLineProps {
  catalog: AutomationCatalog;
  onCatalogChange?: (catalog: AutomationCatalog) => void;
}

export function CurrentSettingsLine({ catalog, onCatalogChange }: CurrentSettingsLineProps) {
  const { t } = useTranslation();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ai = me.user.id !== null && me.user.aiEnabled ? t('automation.on') : t('automation.off');
  const autoItems = catalog.groups.flatMap((g) =>
    g.items.filter((item) => !isAiCatalogueKey(item.key)),
  );
  const autoOn = autoItems.length === 0 || autoItems.every((item) => item.enabled);
  const auto = autoOn ? t('automation.on') : t('automation.off');

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {t('automation.currentSettings', { ai, auto })}
        </Typography>
        <Button size="small" onClick={() => setOpen(true)}>
          {t('automation.change')}
        </Button>
      </Stack>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('consent.title')}</DialogTitle>
        <DialogContent>
          <ConsentStep
            variant="compact"
            catalog={catalog}
            onCatalogChange={onCatalogChange}
            onSaved={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
